// ¿Hay algo abierto ENCIMA del contenido? — ADR-2026-09-13-el-flotante-se-va-si-hay-algo-abierto.
//
// Lo usa el botón flotante "Preguntále a Atlas" para no quedarse en la esquina tapando el
// panel que el usuario acaba de abrir (lo reportó Mati con "Agregar producto": el pill se
// le montaba sobre el botón del panel).
//
// Por qué se DETECTA y no se avisa: hay 157 archivos con `fixed inset-0` y ninguna
// primitiva de modal compartida, así que pedirle a cada uno que se registre deja el bug
// vivo en todos los que falten — y el usuario los va encontrando de a uno. Detectar cubre
// los que existen y los que vengan, sin tocar ninguno.
//
// Dos señales, y con que alcance una basta:
//
//  1. Un elemento a pantalla completa por encima del contenido: en este proyecto eso se
//     escribe SIEMPRE con las clases `fixed inset-0`, así que un `querySelector` las
//     encuentra. Se ignoran los que no reciben clicks (`pointer-events: none`): esos son
//     capas decorativas —degradados, velos de transición— que no tapan nada.
//  2. El scroll del body bloqueado, que es como este proyecto marca "hay algo abierto"
//     desde antes (13 archivos, incluido `InterfazV2` mientras un modo está abierto). Cubre
//     los paneles que no son `inset-0`, como el sheet de los modos.
//
// El sesgo es a ESCONDER: si hay duda, el botón no está y el chat se sigue abriendo desde
// la sidebar. Al revés —quedarse cuando tapa algo— es el bug que esto arregla.
import { useEffect, useState } from 'react';

/** Una capa a pantalla completa, reducida a lo que hace falta para decidir. */
export interface CapaObservada {
  pointerEvents: string;
  display: string;
  visibility: string;
}

/**
 * LA REGLA, sin DOM: se saca aparte para poder testearla (vitest corre en `node`, sin
 * jsdom, así que lo que toca el documento no se puede fijar en un test).
 */
export function tapaAlgo(p: { bodyOverflow: string; capas: readonly CapaObservada[] }): boolean {
  if (p.bodyOverflow === 'hidden') return true;
  return p.capas.some(
    (c) =>
      // Una capa que no recibe clicks no tapa nada: es decorativa.
      c.pointerEvents !== 'none' && c.display !== 'none' && c.visibility !== 'hidden',
  );
}

/** La LECTURA: mira el documento y le pasa los hechos a la regla. */
function hayAlgoAbierto(): boolean {
  if (typeof document === 'undefined') return false;
  const capas = [...document.querySelectorAll<HTMLElement>('.fixed.inset-0')].map((el) => {
    const cs = window.getComputedStyle(el);
    return { pointerEvents: cs.pointerEvents, display: cs.display, visibility: cs.visibility };
  });
  return tapaAlgo({ bodyOverflow: document.body.style.overflow, capas });
}

export function useHayAlgoAbiertoEncima(): boolean {
  const [abierto, setAbierto] = useState(hayAlgoAbierto);

  useEffect(() => {
    let pedido = 0;
    const revisar = () => {
      pedido = 0;
      setAbierto(hayAlgoAbierto());
    };
    // Se agrupa por frame: montar un modal dispara varias mutaciones seguidas y no tiene
    // sentido leer estilos computados una vez por cada una.
    const agendar = () => {
      if (pedido) return;
      pedido = requestAnimationFrame(revisar);
    };
    const observer = new MutationObserver(agendar);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      // `style` del body es el candado de scroll; `class` en el subtree cubre los overlays
      // que ya están montados y se prenden cambiando de clase.
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
    revisar();
    return () => {
      observer.disconnect();
      if (pedido) cancelAnimationFrame(pedido);
    };
  }, []);

  return abierto;
}

export default useHayAlgoAbiertoEncima;
